import { ProductTile } from './index';

export class ProductTileBuilder {
  constructor(title, productType) {
    this.title = title;
    this.productType = productType;
  }

  /**
   * Set up properties like the node where Product Tile instance will be render and product id value
   * @param {String} productId - product id value
   * @param {HTMLElement} parentNode - parent node where Product Tile instances will be contained
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  makeNode(productId, parentNode) {
    const productTileNode = document.createElement('product-tile');
    productTileNode.classList.add('ProductTile', 'ProductTile--react');
    productTileNode.setAttribute('data-product-id', productId);
    productTileNode.setAttribute('data-product-title', this.title);
    this.productTileNode = productTileNode;
    this.pid = productId;
    parentNode.appendChild(productTileNode);
    return this;
  }

  /**
   * Adjust a previous url with new values and set it as a property
   * @param {String} rawUrl - previous product url value
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  makeUrl(rawUrl) {
    const productUrl = rawUrl.replace('https://windsor-us.myshopify.com', '');
    this.productUrl = productUrl;
    return this;
  }

  /**
   * Choose the first variant that matches with the color and set up a couple of properties
   * such as imageUrl, imageAlt, and variants
   * @param {String} color - main product color
   * @param {Object[]} variants - array of variant objects
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  selectColor(color, variants) {
    this.selectedColor = color;

    const correctVariant = variants.find((variant) => color.toLowerCase() === variant.Color.toLowerCase());
    if (correctVariant) {
      this.imageUrl = correctVariant.image_url;
      this.imageAlt = this.title;
      this.variants = variants;
    }

    return this;
  }

  /**
   * Build an Array of color objects based on Sellable variants
   * @param {String[]} colors - array of available colors in the product
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  makeOptions(colors) {
    this.options = [];
    colors.forEach((color) => {
      const firstVariant = this.variants.find(
        (variant) => variant.Color.toLowerCase() === color.toLowerCase() && variant.Sellable,
      );
      if (!firstVariant) return;

      this.options.push({
        Barcode: firstVariant.barcode,
        Color: color,
        img: firstVariant.image_url,
        size: firstVariant.option2,
        variantId: firstVariant.variant_id,
      });
    });
    return this;
  }

  /**
   * Set up price's properties such as price, compare at price and max compare at price on ProductTileBuilder
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  makePrices() {
    const firstVariantSellable = this.variants.find((variant) => variant.Sellable);
    const maximumCompareAtPrice = Math.max(...this.variants.map((variant) => variant.compare_at_price));
    this.price = firstVariantSellable.price;
    this.compareAtPrice = firstVariantSellable.compare_at_price;
    this.maxCompareAtPrice = maximumCompareAtPrice;
    return this;
  }

  /**
   * Set up tags property to render badges on ProductTile instance
   * @param {String} tags - available tags on the product
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  makeBadges(tags) {
    this.tags = tags.split(',').map((tag) => tag.toLowerCase());
    return this;
  }

  /**
   * Set up a reviews property on ProductTileBuilder instance in order to turn on reviews section on ProductTile instance
   * @param {Number} reviews - number of reviews by product
   * @returns {ProductTileBuider} - ProductTileBuilder object
   */
  makeReviews(reviews) {
    // Set the number of stars
    const rating = Math.round((reviews / 20) * 2) / 2;
    this.reviews = rating;
    return this;
  }

  /**
   * Build a new ProductTile instance according to ProductTileBuilder's properties
   * @returns {ProductTile} - return a ProductTile instance
   */
  build() {
    return new ProductTile(this);
  }
}
